import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { watchAuthState, signOutUser, isSocialSigningIn } from '../services/authService';
import { signInWithCustomToken, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';
import { APP_CONFIG } from '../config/appConfig';
import { getUserProfileByUid } from '../services/userProfileService';
import { saveFcmToken, removeFcmToken } from '../services/fcmTokenService';
import { postToRN, isInRnWebView, requestPushToken, onPushToken, appendRnLog, listenWebviewMessages } from '../bridge/webviewBridge';
import { requestWebPushToken, onForegroundMessage } from '../services/webPushService';

// Firebase Console > Cloud Messaging > Web Push certificates
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || '';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);

  // FCM 토큰 보류 ref (userData 로드 전에 토큰이 먼저 올 수 있음)
  const pendingFcmToken = useRef(null);

  const addDebug = useCallback((msg) => {
    if (!debugMode) return;
    setDebugInfo((prev) => [...prev.slice(-49), `[${new Date().toLocaleTimeString()}] ${msg}`]);
  }, [debugMode]);

  useEffect(() => {
    const unsubscribe = watchAuthState(async (firebaseUser) => {
      if (firebaseUser) {
        setCurrentUser(firebaseUser);
        try {
          const profile = await getUserProfileByUid(firebaseUser.uid);
          setUserData(profile || {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || '',
            phoneE164: '',
            phoneVerified: false,
            provider: '',
            status: '',
          });
        } catch (err) {
          console.error('프로필 로드 실패:', err);
          setUserData({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || '',
            phoneE164: '',
            phoneVerified: false,
            provider: '',
            status: '',
          });
        }
        // RN WebView일 때 FCM 토큰 요청
        if (isInRnWebView()) {
          requestPushToken();
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // FCM 토큰 수신 → Firestore 저장 (userData.uid = Firestore 문서 ID 사용)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = onPushToken((token, platform) => {
      pendingFcmToken.current = { token, platform };
      const firestoreUid = userData?.uid || currentUser?.uid;
      if (firestoreUid) {
        console.log('[FCM] 토큰 즉시 저장:', firestoreUid, token?.slice(0, 20));
        saveFcmToken(firestoreUid, token, platform).catch(() => {});
      } else {
        console.log('[FCM] uid 없음 — 토큰 보류:', token?.slice(0, 20));
      }
    });
    return off;
  }, [currentUser, userData]);

  // 소셜 로그인 결과 처리 (유일한 핸들러 — localStorage + 메시지 리스너)
  const signinHandled = useRef(false);
  const processSocialResult = useCallback(async (msg) => {
    if (!msg?.success || signinHandled.current) return;
    // LoginPage의 handleSocial이 이미 처리 중이면 건너뜀 (중복 방지)
    if (isSocialSigningIn()) return;
    signinHandled.current = true;
    console.log('[AuthContext] 소셜 로그인 처리:', msg.provider);
    try {
      if (msg.provider === 'kakao' && msg.accessToken) {
        const cfUrl = APP_CONFIG.kakaoCustomTokenUrl;
        if (cfUrl) {
          const res = await fetch(cfUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken: msg.accessToken }),
          });
          const d = await res.json();
          if (d.customToken) {
            await signInWithCustomToken(auth, d.customToken);
          }
        }
      } else if (msg.provider === 'apple' && msg.identityToken) {
        const cfUrl = APP_CONFIG.appleCustomTokenUrl;
        if (cfUrl) {
          const res = await fetch(cfUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identityToken: msg.identityToken, displayName: msg.displayName || '' }),
          });
          const d = await res.json();
          if (d.customToken) {
            await signInWithCustomToken(auth, d.customToken);
          }
        }
      } else if (msg.provider === 'google' && msg.idToken) {
        // 네이티브 구글 로그인 결과 → Firebase 자격증명으로 교환
        const credential = GoogleAuthProvider.credential(msg.idToken, msg.accessToken);
        await signInWithCredential(auth, credential);
      }
    } catch (err) {
      console.error('[AuthContext] 소셜 로그인 처리 실패:', err);
      signinHandled.current = false;
    }
  }, []);

  // 1) 마운트 시 localStorage 확인 (forceSend가 먼저 실행된 경우)
  useEffect(() => {
    if (!isInRnWebView()) return;
    try {
      const raw = localStorage.getItem('__pendingSigninResult');
      if (!raw) return;
      localStorage.removeItem('__pendingSigninResult');
      const data = JSON.parse(raw);
      const msg = data?.payload || data;
      processSocialResult(msg);
    } catch {}
  }, [processSocialResult]);

  // 2) 메시지 리스너 (forceSend가 나중에 실행된 경우 — React 마운트 후 도착)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = listenWebviewMessages((msg) => {
      if (msg.type === 'SIGNIN_RESULT') {
        // localStorage도 정리
        try { localStorage.removeItem('__pendingSigninResult'); } catch {}
        const payload = msg.payload || msg;
        processSocialResult(payload);
      }
    });
    return off;
  }, [processSocialResult]);

  // RN 브릿지 메시지 로깅 (디버그용)
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = listenWebviewMessages((msg) => {
      const type = msg.type || '';
      const pp = { ...msg };
      delete pp.type;
      const tokenHint = pp.token ? `tk:${String(pp.token).slice(0,15)}...` : '';
      appendRnLog(type, Object.keys(pp), tokenHint);

      if (type === 'TOGGLE_DEBUG' || type === 'DEBUG_MODE') {
        setDebugMode((prev) => !prev);
      }
      if (type === 'PUSH_TOKEN' || type === 'FCM_TOKEN') {
        addDebug(`FCM token: ${msg.token?.slice(0, 20)}...`);
      }
    });
    return off;
  }, [addDebug]);

  // userData 로드 완료 시 보류중인 FCM 토큰 저장 + 재요청
  useEffect(() => {
    if (!userData?.uid) return;

    if (isInRnWebView()) {
      // RN: 보류중인 토큰이 있으면 저장
      if (pendingFcmToken.current) {
        const { token, platform } = pendingFcmToken.current;
        console.log('[FCM] 보류 토큰 저장:', userData.uid, token?.slice(0, 20));
        saveFcmToken(userData.uid, token, platform).catch(() => {});
      } else {
        console.log('[FCM] userData 로드됨 — 토큰 재요청');
        requestPushToken();
      }
    } else if (VAPID_KEY) {
      // 웹 브라우저: 웹 푸시 토큰 발급
      requestWebPushToken(userData.uid, VAPID_KEY).catch(() => {});
    }
  }, [userData?.uid]);

  // 웹 브라우저 포그라운드 메시지 수신
  useEffect(() => {
    if (isInRnWebView() || !VAPID_KEY) return;
    const unsub = onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      if (title && Notification.permission === 'granted') {
        new Notification(title, { body: body || '', icon: '/logo192.png' });
      }
    });
    return () => unsub();
  }, []);

  /** userDoc 리로드 (폰 연동 등 후 갱신용) */
  const refreshUser = useCallback(async () => {
    if (!currentUser) return;
    try {
      const profile = await getUserProfileByUid(currentUser.uid);
      if (profile) setUserData(profile);
    } catch (err) {
      console.error('refreshUser 실패:', err);
    }
  }, [currentUser]);

  const login = (data) => {
    setUserData(data);
  };

  const logout = async () => {
    // 로그아웃 시 FCM 토큰 삭제 (Firestore 문서 ID 사용)
    const firestoreUid = userData?.uid || currentUser?.uid;
    if (firestoreUid) {
      removeFcmToken(firestoreUid).catch(() => {});
    }
    // 자동 로그인 정보 삭제
    localStorage.removeItem('classcare.autoLogin');
    if (isInRnWebView()) postToRN("START_SIGNOUT");
    await signOutUser();
    setCurrentUser(null);
    setUserData(null);
  };

  /** 로그인 여부 계산 */
  const isLoggedIn = !!(currentUser && userData);

  const value = {
    currentUser,
    userData,
    loading,
    isLoggedIn,
    login,
    logout,
    refreshUser,
    debugMode,
    debugInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
